import { BASE_URL, LOCAL_BASE_URL } from '../constants/constants';

import CryptoJS from 'crypto-js';
import axios from 'axios';
import { logger } from '../utils/logger';
import { performLogout } from '../utils/performLogout';

const baseEndPoint = process.env.REACT_APP_API_URL;
// Encryption key - in production, this should be stored securely
// and possibly retrieved from environment variables

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

const USE_ENCRYPTION = false;
// Encryption function
const encryptData = (data) => {
	if (!data) return data;
	const jsonString = JSON.stringify(data);
	return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
};

// Decryption function
const decryptData = (encryptedData) => {
	if (!encryptedData) return encryptedData;
	const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
	return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

const axiosRequestInterceptor = axios.create({
	baseURL: baseEndPoint,
	withCredentials: true,
	headers: {
		'Content-Type': 'application/json',
	},
});

axiosRequestInterceptor.interceptors.request.use(
	(config) => {
		// const token = localStorage.getItem("authToken");

		logger.log('API Request (before encryption):', {
			url: config.url,
			method: config.method,
			data: config.data,
			headers: config.headers,
		});

		const isFormData = config.data instanceof FormData;

		// Encrypt the request data if it exists and is not FormData
		if (USE_ENCRYPTION && config.data && !isFormData && !config._isEncrypted) {
			config.data = { encryptedData: encryptData(config.data) };
			config._isEncrypted = true;
		}

		// if (token) {
		//   config.headers.Authorization = `Bearer ${token}`;
		//   logger.log("Token Added");
		// }
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

axiosRequestInterceptor.interceptors.response.use(
	(response) => {
		logger.log('API Response (before decryption):', response);

		// Decrypt the response data if it contains encrypted data
		if (USE_ENCRYPTION && response.data && response.data.encryptedData) {
			response.data = decryptData(response.data.encryptedData);
			logger.log('API Response (after decryption):', response);
		}

		return response;
	},
	async (error) => {
		const originalRequest = error.config;
		if (!originalRequest?._retryCount) {
			originalRequest._retryCount = 0;
		}
		if (error.response && error.response.status === 403 && originalRequest._retryCount < 3) {
			originalRequest._retryCount += 1;
			logger.log(
				'Access token expired. Attempting refresh... Refresh attemp #' +
					originalRequest._retryCount
			);

			try {
				await axiosRequestInterceptor.post('/users/refresh'); // ✅ Refresh Token Request
				return axiosRequestInterceptor(error.config); // ✅ Retry the original request
			} catch (err) {
				logger.log('Token refresh failed. Logging out.');
				// window.location.href = "/login"; // Redirect to login page
				await performLogout();
			}
		}

		if (originalRequest._retryCount >= 3) {
			console.warn('🚪 Logging out after too many refresh attempts');
			await performLogout();
		}

		// Attempt to decrypt error response if it's encrypted
		if (USE_ENCRYPTION && error.response?.data?.encryptedData) {
			try {
				error.response.data = decryptData(error.response.data.encryptedData);
			} catch (decryptionError) {
				logger.log('Error decrypting error response:', decryptionError);
			}
		}

		logger.log('API Error:', {
			message: error.message,
			response: error.response?.data,
			status: error.response?.status,
			statusText: error.response?.statusText,
		});
		return Promise.reject(error);
	}
);

export default axiosRequestInterceptor;
